import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import './StockChart.css';
import { ChartConfig } from '../screener/ScreenerTable.tsx';
import screenerApi, { CandleResponse } from '../../services/screenerApi.ts';
import StockChartWithDrawings from './StockChartWithDrawings.tsx';
import { ScreenerStockResponse } from '../../services/screenerApi.ts';
import { isAIScreener as checkIsAI } from '../../config/screenerConfig.ts';

// ── Drawing types ─────────────────────────────────────────────────────────────

type DrawingTool =
  | 'CURSOR' | 'TREND_LINE' | 'ARROW' | 'H_LINE' | 'V_LINE'
  | 'H_RAY_RIGHT' | 'H_RAY_LEFT' | 'RECTANGLE' | 'FREEHAND' | 'ARC' | 'TEXT';

interface ChartPoint { time: string; price: number; }

interface Drawing {
  id: string;
  type: DrawingTool;
  points: ChartPoint[];
  color: string;
  lineWidth: number;
  text?: string;
  meta?: Record<string, unknown>;
}

function drawEllipseArc(
  ctx: CanvasRenderingContext2D,
  p0: { x: number; y: number },   // left endpoint (pixels)
  p1: { x: number; y: number },   // right endpoint (pixels)
  apex: { x: number; y: number }, // apex point (pixels)
) {
  const adx = p1.x - p0.x;
  const ady = p1.y - p0.y;
  const chordLen = Math.hypot(adx, ady);
  if (chordLen < 1) return;
  const perpX = -ady / chordLen;
  const perpY =  adx / chordLen;
  const midX = (p0.x + p1.x) / 2;
  const midY = (p0.y + p1.y) / 2;
  const h = (apex.x - midX) * perpX + (apex.y - midY) * perpY;
  if (Math.abs(h) < 0.5) return;
  ctx.ellipse(midX, midY, chordLen / 2, Math.abs(h), Math.atan2(ady, adx), Math.PI, 0, h < 0);
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface StockChartWithDrawingsCardProps {
  symbol?: string;
  exchange?: string;
  timeframe?: string;
  width?: number;
  height?: number;
  capturedPrice?: string;
  capturedOn?: string;
  chartConfig?: ChartConfig;
  candles?: CandleResponse[] | null;
  stockId: number;
  screenerCode: string;
  smaValues?: number[];
  resistanceLevel?: number | null;
  supportLevel?: number | null;
}

const SMA_COLORS = ['#FF6B00', '#2962FF', '#00C853', '#FF4081', '#AA00FF'];

// ── Component ─────────────────────────────────────────────────────────────────

const StockChartWithDrawingsCard: React.FC<StockChartWithDrawingsCardProps> = ({
  symbol = 'RELIANCE',
  exchange = 'NSE',
  timeframe = '1D',
  width = 800,
  height = 400,
  capturedPrice,
  capturedOn,
  chartConfig,
  candles,
  stockId,
  screenerCode,
  smaValues = [],
  resistanceLevel = null,
  supportLevel = null,
}) => {
  const isAIScreener  = checkIsAI(screenerCode);
  const containerRef  = useRef<HTMLDivElement>(null);
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const chartRef      = useRef<any>(null);
  const seriesRef     = useRef<any>(null);
  const chartReadyRef = useRef(false);

  const [priceData, setPriceData]   = useState({ open: 0, high: 0, low: 0, close: 0, prevClose: 0 });
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('dark-theme'));
  const [drawings, setDrawings]     = useState<Drawing[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const drawingsRef = useRef<Drawing[]>([]);
  drawingsRef.current = drawings;

  // Track dark mode
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDarkMode(document.body.classList.contains('dark-theme')));
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Fetch drawings — same as MiniChartWithDrawings
  useEffect(() => {
    if (!screenerCode || !stockId) return;
    screenerApi.getStockDrawings(screenerCode, stockId)
      .then(res => setDrawings(Array.isArray(res.data.drawings) ? res.data.drawings as Drawing[] : []))
      .catch(() => {});
  }, [screenerCode, stockId]);

  // ── Canvas renderer — identical pattern to MiniChartWithDrawings ──────────

  const renderDrawings = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !chartRef.current || !seriesRef.current) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Clip to plot area so drawings never overlap the right price scale
    const psWidth = (chartRef.current.priceScale('right').width?.() ?? 0);
    const plotWidth = canvas.width - psWidth;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, plotWidth, canvas.height);
    ctx.clip();

    drawingsRef.current.forEach(d => {
      ctx.strokeStyle = d.color;
      ctx.lineWidth   = Math.max(1, d.lineWidth);
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      switch (d.type) {
        case 'H_LINE': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (y == null) break;
          ctx.beginPath(); ctx.moveTo(0, Math.round(y)); ctx.lineTo(canvas.width, Math.round(y)); ctx.stroke();
          break;
        }
        case 'H_RAY_RIGHT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath(); ctx.moveTo(Math.round(x), Math.round(y)); ctx.lineTo(canvas.width, Math.round(y)); ctx.stroke();
          break;
        }
        case 'H_RAY_LEFT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath(); ctx.moveTo(0, Math.round(y)); ctx.lineTo(Math.round(x), Math.round(y)); ctx.stroke();
          break;
        }
        case 'V_LINE': {
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (x == null) break;
          ctx.beginPath(); ctx.moveTo(Math.round(x), 0); ctx.lineTo(Math.round(x), canvas.height); ctx.stroke();
          break;
        }
        case 'TEXT': {
          const tx = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          const ty = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (tx == null || ty == null) break;
          ctx.fillStyle = d.color;
          ctx.font = `bold ${10 + d.lineWidth * 2}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(d.text ?? '', Math.round(tx), Math.round(ty));
          break;
        }
        default: {
          const pts = d.points.map(p => {
            const x = chartRef.current.timeScale().timeToCoordinate(p.time);
            const y = seriesRef.current.priceToCoordinate(p.price);
            if (x == null || y == null) return null;
            return { x: Math.round(x), y: Math.round(y) };
          }).filter(Boolean) as { x: number; y: number }[];

          switch (d.type) {
            case 'TREND_LINE': {
              if (pts.length < 2) break;
              ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); ctx.stroke();
              break;
            }
            case 'ARROW': {
              if (pts.length < 2) break;
              const [p0, p1] = pts;
              const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
              const headLen = Math.max(8, d.lineWidth * 3);
              ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p1.x - headLen * Math.cos(angle - Math.PI / 6), p1.y - headLen * Math.sin(angle - Math.PI / 6));
              ctx.lineTo(p1.x - headLen * Math.cos(angle + Math.PI / 6), p1.y - headLen * Math.sin(angle + Math.PI / 6));
              ctx.closePath(); ctx.fillStyle = d.color; ctx.fill();
              break;
            }
            case 'RECTANGLE': {
              if (pts.length < 2) break;
              const rx = Math.min(pts[0].x, pts[1].x), ry = Math.min(pts[0].y, pts[1].y);
              const rw = Math.abs(pts[1].x - pts[0].x), rh = Math.abs(pts[1].y - pts[0].y);
              ctx.strokeRect(rx, ry, rw, rh);
              ctx.save(); ctx.globalAlpha = 0.13; ctx.fillStyle = d.color; ctx.fillRect(rx, ry, rw, rh); ctx.restore();
              break;
            }
            case 'ARC': {
              if (pts.length < 3) break;
              ctx.beginPath(); drawEllipseArc(ctx, pts[0], pts[1], pts[2]); ctx.closePath();
              ctx.save(); ctx.globalAlpha = 0.13; ctx.fillStyle = d.color; ctx.fill(); ctx.restore();
              ctx.beginPath(); drawEllipseArc(ctx, pts[0], pts[1], pts[2]); ctx.stroke();
              break;
            }
            case 'FREEHAND': {
              if (pts.length < 2) break;
              ctx.lineJoin = 'round'; ctx.lineCap = 'round';
              ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
              for (let i = 1; i < pts.length - 1; i++) {
                const mx = (pts[i].x + pts[i + 1].x) / 2;
                const my = (pts[i].y + pts[i + 1].y) / 2;
                ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
              }
              ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
              ctx.stroke();
              break;
            }
          }
        }
      }
    });

    ctx.restore(); // end plot-area clip
  }, []);

  // Keep latest renderDrawings in ref — same pattern as MiniChartWithDrawings
  const renderRef = useRef(renderDrawings);
  renderRef.current = renderDrawings;

  const hasValidCandles = candles && candles.length > 0;

  // ── Build chart ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || !hasValidCandles) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth || width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(197, 203, 206, 0.1)' },
        horzLines: { color: 'rgba(197, 203, 206, 0.1)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: 'rgba(197, 203, 206, 0.3)' },
      timeScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 8,
        barSpacing: 10,
      },
    });
    chartRef.current = chart;

    const candleSeries = (chart as any).addCandlestickSeries({
      upColor: '#26a69a', downColor: '#ef5350', borderVisible: false,
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = candleSeries;

    const chartData = candles!.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }));
    candleSeries.setData(chartData);

    const calcSMA = (data: any[], period: number) => {
      const result = [];
      for (let i = period - 1; i < data.length; i++) {
        let sum = 0;
        for (let j = 0; j < period; j++) sum += data[i - j].close;
        result.push({ time: data[i].time, value: sum / period });
      }
      return result;
    };

    smaValues.forEach((period, idx) => {
      const series = (chart as any).addLineSeries({
        color: SMA_COLORS[idx % SMA_COLORS.length],
        lineWidth: 1,
        title: `SMA ${period}`,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      series.setData(calcSMA(chartData, period));
    });

    // Captured price helpers
    const parseDateStr = (dateStr: string): string | null => {
      try {
        const months: { [k: string]: number } = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
        const p = dateStr.split(' ');
        if (p.length !== 3) return null;
        const d = new Date(+p[2], months[p[1]], +p[0]);
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      } catch { return null; }
    };

    if (chartConfig?.capturedPriceLine && capturedPrice && capturedOn) {
      const pv = parseFloat(capturedPrice.replace(/[^0-9.-]+/g, ''));
      const dt = parseDateStr(capturedOn);
      if (dt) {
        const idx = chartData.findIndex((d: any) => d.time === dt);
        if (idx !== -1) {
          const lineData: any[] = [];
          for (let i = Math.max(0, idx - 4); i <= idx; i++) lineData.push({ time: chartData[i].time, value: pv });
          for (let i = idx + 1; i <= Math.min(chartData.length - 1, idx + 3); i++) lineData.push({ time: chartData[i].time, value: pv });
          if (lineData.length > 0) {
            const pLine = (chart as any).addLineSeries({ color: '#b300faff', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
            pLine.setData(lineData);
            // Anchor marker 6 candles before captured date
            const markerIdx = Math.max(0, idx - 5);
            const markerTime = chartData[markerIdx].time;
            const markerAnchor = (chart as any).addLineSeries({ color: 'rgba(0,0,0,0)', lineWidth: 0, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
            markerAnchor.setData([{ time: markerTime, value: pv * 1.20 }]);
            markerAnchor.setMarkers([
              { time: markerTime, position: 'aboveBar', color: '#9C27B0', shape: 'text', text: `Captured at ₹${pv.toFixed(2)}` },
              { time: markerTime, position: 'aboveBar', color: '#888888', shape: 'text', text: `📅 ${capturedOn}` },
            ]);
          }
        }
      }
    }

    if (chartConfig?.capturedPriceRectangle && capturedPrice && capturedOn) {
      const pv = parseFloat(capturedPrice.replace(/[^0-9.-]+/g, ''));
      const dt = parseDateStr(capturedOn);
      if (dt) {
        const idx = chartData.findIndex((d: any) => d.time === dt);
        if (idx !== -1) {
          const rectData: any[] = [];
          for (let i = Math.max(0, idx - 4); i <= Math.min(chartData.length - 1, idx + 3); i++) rectData.push({ time: chartData[i].time, value: pv });
          if (rectData.length > 0) {
            const fill = isDarkMode ? 'rgba(34,211,238,0.18)' : 'rgba(179,0,250,0.15)';
            const rs = (chart as any).addBaselineSeries({ baseValue: { type: 'price', price: pv * 0.95 }, topFillColor1: fill, topFillColor2: fill, topLineColor: 'rgba(0,0,0,0)', bottomFillColor1: 'rgba(0,0,0,0)', bottomFillColor2: 'rgba(0,0,0,0)', bottomLineColor: 'rgba(0,0,0,0)', lineWidth: 0, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
            rs.setData(rectData);
          }
        }
      }
    }

    // Volume
    const vol = (chart as any).addHistogramSeries({ priceFormat: { type: 'volume' }, priceScaleId: '' });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    vol.setData(candles!.map(c => ({ time: c.time, value: c.volume || 0, color: c.close > c.open ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)' })));

    // S/R lines — AI screeners only
    if (isAIScreener) {
      if (resistanceLevel != null) candleSeries.createPriceLine({ price: resistanceLevel, color: '#f59e0b', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'R' });
      if (supportLevel    != null) candleSeries.createPriceLine({ price: supportLevel,    color: '#3b82f6', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'S' });
    }

    // OHLC header data
    const last = chartData[chartData.length - 1];
    const prev = chartData[chartData.length - 2];
    setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });

    chart.subscribeCrosshairMove((param: any) => {
      if (!param?.time) {
        setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });
        return;
      }
      if (param.seriesData?.size > 0) {
        const d = param.seriesData.values().next().value;
        if (d?.open !== undefined) {
          const i = chartData.findIndex((c: any) => c.time === param.time);
          setPriceData({ open: d.open, high: d.high, low: d.low, close: d.close, prevClose: i > 0 ? chartData[i-1].close : d.close });
        }
      }
    });

    // Sync canvas size — mirrors modal exactly
    const syncCanvas = () => {
      const c = containerRef.current;
      if (!c) return;
      const cw = Math.round(c.getBoundingClientRect().width) || c.clientWidth;
      if (canvasRef.current) { canvasRef.current.width = cw; canvasRef.current.height = height; }
      if (chartRef.current)  chartRef.current.applyOptions({ width: cw });
    };
    syncCanvas();

    // Exact same subscriptions as the working modal
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => renderRef.current());
    chart.timeScale().subscribeVisibleTimeRangeChange(()   => renderRef.current());

    // mousemove keeps drawings in sync while dragging — key missing piece from modal
    const onMove = () => renderRef.current();
    containerRef.current.addEventListener('mousemove', onMove);

    const ro = new ResizeObserver(() => { syncCanvas(); renderRef.current(); });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      containerRef.current?.removeEventListener('mousemove', onMove);
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
      chartReadyRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, capturedPrice, capturedOn, chartConfig?.capturedPriceLine, chartConfig?.capturedPriceRectangle, isDarkMode]);

  // Re-render drawings when they arrive
  useEffect(() => {
    renderRef.current();
  }, [drawings]);

  const change        = priceData.close - priceData.prevClose;
  const changePercent = priceData.prevClose > 0 ? (change / priceData.prevClose) * 100 : 0;
  const isPositive    = change >= 0;
  const isBullish     = priceData.close >= priceData.open;
  const tf            = timeframe === 'Daily' ? 'D' : timeframe === 'Weekly' ? 'W' : timeframe === 'Monthly' ? 'M' : timeframe;

  if (!hasValidCandles) {
    return (
      <div className="stock-chart-wrapper">
        <div className="stock-chart-header">
          <div className="symbol-info">
            <span className="symbol-name">{symbol}</span>
            <span className="symbol-timeframe">{tf}</span>
            <span className="symbol-exchange">{exchange}</span>
          </div>
        </div>
        <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4, color: '#888', fontSize: 14, flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 24 }}>📊</span>
          <span>No chart data available</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="stock-chart-wrapper">
        {/* Header */}
        <div className="stock-chart-header">
          <div className="symbol-info">
            <span className="symbol-name">{symbol}</span>
            <span className="symbol-timeframe">{tf}</span>
            <span className="symbol-exchange">{exchange}</span>
          </div>
          <div className="price-info">
            <span className="price-pair"><span className="price-label">O</span><span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.open.toFixed(2)}</span></span>
            <span className="price-pair"><span className="price-label">H</span><span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.high.toFixed(2)}</span></span>
            <span className="price-pair"><span className="price-label">L</span><span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.low.toFixed(2)}</span></span>
            <span className="price-pair"><span className="price-label">C</span><span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.close.toFixed(2)}</span></span>
            <span className={`stock-price-change ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
            </span>
            {/* Fullscreen icon */}
            <button
              onClick={() => setIsFullscreen(true)}
              title="Fullscreen"
              className="scdc-fullscreen-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--chart-label)', display: 'flex', alignItems: 'center' }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 1h5v1H2v4H1V1zm9 0h5v5h-1V2h-4V1zM1 10h1v4h4v1H1v-5zm13 4h-4v1h5v-5h-1v4z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Chart + canvas overlay */}
        <div style={{ position: 'relative' }}>
          <div ref={containerRef} className="stock-chart-container" />
          <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
        </div>
      </div>

      {/* Same modal as "View Analysis" button */}
      {isFullscreen && (
        <StockChartWithDrawings
          stock={{
            id: stockId,
            symbol,
            stockName: symbol,
            exchange,
            timeframe,
            capturedPrice: capturedPrice ? parseFloat(capturedPrice.replace(/[^0-9.-]+/g, '')) : 0,
            capturedOn: capturedOn ?? '',
            candles: candles ?? null,
            currentPrice: null, maxUpside: null, maxUpsidePrice: null,
            maxUpsideDate: null, currentUpside: null, sector: null,
            industry: null, marketCap: null, volume: null, chartUrl: null,
            stockRank: 0, notes: null, eliminated: null, hiddenForFutureUsers: null,
            resistanceLevel, supportLevel,
          } as ScreenerStockResponse}
          drawings={drawings}
          isAIScreener={isAIScreener}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </>
  );
};

export default StockChartWithDrawingsCard;
